import React, { useEffect, useState } from 'react';
import { Tabs, TabPane, Text, theme } from 'components';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { buildCssProps } from 'utils';
import FeedbackRequest, { FeedbackRequestProps } from './FeedbackRequest';
import FeedbackSurvey, { FeedbackSurveyProps } from './FeedbackSurvey';
import FeedbackSettingsLayout, {
  FeedbackSettingsLayoutProps,
} from './FeedbackSettingsLayout';

type FeedbackSettingsProps = FeedbackSurveyProps &
  FeedbackRequestProps &
  FeedbackSettingsLayoutProps;

enum FeedbackTab {
  Survey = 'Survey',
  Request = 'Request',
}

const FeedbackSettings: React.FC<FeedbackSettingsProps> = ({
  updateTemplate,
  template,
  listing,
  showWarning,
  feedbackCollecting,
  saveSettings,
  hasLimitedAccess,
  surveyTemplates,
  setSurveyTemplateId,
  surveyTemplateId,
  setIsCustomSelected,
  ...feedbackRequestProps
}) => {
  const [key, setKey] = useState<FeedbackTab>(FeedbackTab.Request);
  const [isInvalidSurvey, setIsInvalidSurvey] = useState(false);
  const { md } = useBreakpoint();
  const tabs = ['Request', 'Survey'];

  useEffect(() => {
    if (key === 'Survey') {
      setIsInvalidSurvey(template.length === 0);
    } else {
      setIsInvalidSurvey(false);
    }
  }, [key, template, setIsInvalidSurvey]);

  return (
    <FeedbackSettingsLayout
      listing={listing}
      showWarning={showWarning}
      feedbackCollecting={feedbackCollecting}
      saveSettings={saveSettings}
      hasLimitedAccess={hasLimitedAccess}
      isInvalidSurvey={isInvalidSurvey}
    >
      <Tabs
        activeKey={key}
        onChange={(keyValue: FeedbackTab) => {
          setKey(keyValue);
        }}
        tabBarStyle={{ margin: 0, padding: `0 ${md ? 25 : 10}px` }}
      >
        {tabs.map(tab => (
          <TabPane
            tab={
              <StyledTabTile $isActive={key === tab} data-cy={`${tab}-tab`}>
                Feedback {tab}
              </StyledTabTile>
            }
            key={tab}
          >
            <StyledContentContainer $md={md} $blur={hasLimitedAccess}>
              {!feedbackCollecting && <StyledDisabled />}
              <StyledContentWrapper>
                {tab === FeedbackTab.Request ? (
                  <FeedbackRequest
                    {...feedbackRequestProps}
                    setIsCustomSelected={setIsCustomSelected}
                  />
                ) : (
                  <FeedbackSurvey
                    template={template}
                    updateTemplate={updateTemplate}
                    surveyTemplates={surveyTemplates}
                    setSurveyTemplateId={setSurveyTemplateId}
                    surveyTemplateId={surveyTemplateId}
                    setIsCustomSelected={setIsCustomSelected}
                    setIsInvalidSurvey={setIsInvalidSurvey}
                  />
                )}
              </StyledContentWrapper>
            </StyledContentContainer>
          </TabPane>
        ))}
      </Tabs>
    </FeedbackSettingsLayout>
  );
};

const StyledTabTile = styled(Text)<{ $isActive: boolean }>`
  color: ${({ $isActive }) =>
    $isActive ? theme.getColor('primary') : theme.getColor('textSecondary')};
`;

const StyledContentContainer = styled.div<{ $md: boolean; $blur?: boolean }>`
  padding: ${({ $md }) => ($md ? '25px' : '0px')};
  background-color: ${theme.gray4};
  position: relative;
  ${({ $blur }) =>
    buildCssProps([
      ['filter', $blur && `blur(${theme.pageBlurRadius})`],
      ['pointer-events', $blur && 'none'],
    ])}
`;

const StyledContentWrapper = styled.div`
  padding: 25px 25px 50px 25px;
  background-color: white;
`;

const StyledDisabled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 5;
`;

export default FeedbackSettings;
